export const clone = (value) => JSON.parse(JSON.stringify(value));

const uid = () =>
  `el_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const baseStyle = () => ({
  fonte: "dejavu_sans",
  tamanho_pt: 8,
  negrito: false,
  italico: false,
  sublinhado: false,
  alinhamento: "center",
  alinhamento_vertical: "middle",
  cor: "#000000",
  fundo: "transparent",
  cor_borda: "#000000",
  borda_mm: 0,
  padding_mm: 0,
  espacamento_letras_pt: 0,
});

const baseFormat = () => ({
  prefixo: "",
  sufixo: "",
  transformacao: "nenhuma",
  mascara: "automatica",
  casas_decimais: 2,
  limite_caracteres: 0,
  texto_vazio: "",
});

export const createElement = ({ type = "field", field = null, label = {} }) => {
  const width = Number(label.largura_mm) || 50;
  const height = Number(label.altura_mm) || 30;
  const isCode = type === "barcode" || type === "qrcode";
  const elementWidth =
    type === "qrcode" ? Math.min(14, width - 2) : Math.min(30, width - 2);
  let elementHeight =
    type === "qrcode"
      ? Math.min(14, height - 2)
      : isCode
      ? Math.min(10, height - 2)
      : 5;
  const style = baseStyle();

  if (type === "field") style.negrito = field?.format === "moeda";
  if (type === "line") elementHeight = 0.5;
  if (type === "rectangle") style.borda_mm = 0.3;

  const displayRule =
    type === "logo"
      ? "quando_logo"
      : ["line", "rectangle"].includes(type)
      ? "sempre"
      : field?.display_rule || "se_preenchido";

  return {
    id: uid(),
    tipo: type,
    nome:
      field?.label ||
      {
        text: "Texto livre",
        barcode: "Código de barras",
        qrcode: "QR Code",
        logo: "Logo da empresa",
        line: "Linha",
        rectangle: "Caixa / borda",
      }[type] ||
      "Elemento",
    campo:
      field?.key ||
      (type === "barcode"
        ? "produto.codigo_barras_etiqueta"
        : type === "qrcode"
        ? "produto.codigo_barras"
        : null),
    texto: type === "text" ? "Digite seu texto" : null,
    x_mm: Math.max(0, Number(((width - elementWidth) / 2).toFixed(1))),
    y_mm: Math.max(0, Number(((height - elementHeight) / 2).toFixed(1))),
    largura_mm: Math.max(0.5, Number(elementWidth.toFixed(1))),
    altura_mm: Math.max(0.5, Number(elementHeight.toFixed(1))),
    rotacao: 0,
    ordem: Date.now() % 1000,
    visivel: true,
    exibir_quando: displayRule,
    estilo: style,
    formato: {
      ...baseFormat(),
      mascara: field?.format || "automatica",
    },
    ...(type === "field" && field?.key === "produto.preco"
      ? {
          priceMode: "cash",
          installments: 3,
          installmentIncrease: 0,
          commercialEnding: "none",
        }
      : {}),
    codigo_barras: {
      tipo: "auto",
      exibir_texto: true,
      margem: 0,
      modulo_px: 2,
    },
  };
};

export const duplicateElement = (source, label) => {
  const copy = clone(source);
  const maxX = Math.max(
    0,
    Number(label?.largura_mm || 50) - Number(copy.largura_mm || 1)
  );
  const maxY = Math.max(
    0,
    Number(label?.altura_mm || 30) - Number(copy.altura_mm || 1)
  );
  copy.id = uid();
  copy.nome = `${source.nome || "Elemento"} (cópia)`;
  copy.x_mm = Math.min(maxX, Number(copy.x_mm || 0) + 1);
  copy.y_mm = Math.min(maxY, Number(copy.y_mm || 0) + 1);
  copy.ordem = Number(source.ordem || 0) + 1;
  return copy;
};

export const placeElementWithoutOverlap = (
  element,
  existingElements = [],
  label = {},
  gapMm = 0.5
) => {
  const placed = clone(element);
  const labelWidth = Number(label.largura_mm || 50);
  const labelHeight = Number(label.altura_mm || 30);
  const width = Math.min(
    labelWidth,
    Math.max(0.5, Number(placed.largura_mm || 1))
  );
  const height = Math.min(
    labelHeight,
    Math.max(0.5, Number(placed.altura_mm || 1))
  );
  const maxX = Math.max(0, labelWidth - width);
  const maxY = Math.max(0, labelHeight - height);
  const gap = Math.max(0, Number(gapMm || 0));

  const intersects = (x, y) =>
    existingElements.some((candidate) => {
      if (candidate?.visivel === false) return false;
      const candidateX = Number(candidate.x_mm || 0);
      const candidateY = Number(candidate.y_mm || 0);
      const candidateWidth = Math.max(0, Number(candidate.largura_mm || 0));
      const candidateHeight = Math.max(0, Number(candidate.altura_mm || 0));
      return (
        x < candidateX + candidateWidth + gap &&
        x + width + gap > candidateX &&
        y < candidateY + candidateHeight + gap &&
        y + height + gap > candidateY
      );
    });

  const centerX = Number((maxX / 2).toFixed(2));
  const xCandidates = [centerX];
  for (let x = 0.5; x <= maxX + 0.001; x += 0.5) {
    const normalized = Number(x.toFixed(2));
    if (!xCandidates.includes(normalized)) xCandidates.push(normalized);
  }
  if (!xCandidates.includes(0)) xCandidates.push(0);

  const preferredY = Math.min(1, maxY);
  const yCandidates = [preferredY];
  if (maxY >= 0.5 && !yCandidates.includes(0.5)) yCandidates.push(0.5);
  for (let y = 1.5; y <= maxY + 0.001; y += 0.5) {
    yCandidates.push(Number(y.toFixed(2)));
  }
  if (!yCandidates.includes(0)) yCandidates.push(0);

  let available = null;
  for (const x of xCandidates) {
    for (const y of yCandidates) {
      if (!intersects(x, y)) {
        available = { x, y };
        break;
      }
    }
    if (available) break;
  }

  if (!available) return null;
  placed.x_mm = available.x;
  placed.y_mm = available.y;
  placed.largura_mm = width;
  placed.altura_mm = height;
  return placed;
};

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key);

const IMPLICIT_BARCODE_FIELDS = [
  "produto.codigo_barras",
  "produto.codigo_barras_etiqueta",
  "produto.codigo_barras_a4054",
];

const LEGACY_CODE_LEGEND_IDS = new Set(["numero_barras"]);

const isSelectableBarcode = (element, convertedLegacy = false) => {
  if (String(element?.tipo || "") !== "barcode") return false;

  const field = String(element?.campo || "");
  return (
    ["produto.codigo_barras_etiqueta", "produto.codigo_barras_a4054"].includes(
      field
    ) ||
    (field === "produto.codigo_barras" &&
      (convertedLegacy || element.exibir_quando === "quando_codigo_barras"))
  );
};

const activeBarcodeSources = (options = {}) =>
  [
    options.considerar_referencia
      ? { suffix: "referencia", field: "produto.referencia", type: "barcode" }
      : null,
    !hasOwn(options, "cod_barras") || options.cod_barras
      ? { suffix: "ean", field: "produto.codigo_barras", type: "barcode" }
      : null,
    options.considerar_codigo_interno
      ? {
          suffix: "codigo_interno",
          field: "produto.codigo",
          type: options.codigo_interno_somente_numeracao ? "field" : "barcode",
        }
      : null,
  ].filter(Boolean);

const isCodeLegend = (element, convertedLegacy = false) => {
  const id = String(element?.id || "").toLocaleLowerCase("pt-BR");
  return (
    element?.exibir_quando === "quando_codigo_barras" ||
    (convertedLegacy && LEGACY_CODE_LEGEND_IDS.has(id))
  );
};

// Espelha a adaptação temporária feita pelo backend antes de renderizar o PDF.
// A definição salva não muda; somente a representação deste cenário de teste.
export const resolveElementForPrintOptions = (
  element,
  options = {},
  convertedLegacy = false
) => {
  if (!element || typeof element !== "object") return element;
  const mapped = { ...element };
  const type = String(element.tipo || "field");
  const field = String(element.campo || "");
  const internalNumberOnly = Boolean(
    options.considerar_codigo_interno &&
      options.codigo_interno_somente_numeracao
  );

  const selectableBarcode = isSelectableBarcode(element, convertedLegacy);

  if (type === "barcode" && selectableBarcode) {
    mapped.tipo = internalNumberOnly ? "field" : "barcode";
    mapped.campo = internalNumberOnly
      ? "produto.codigo_legenda_etiqueta"
      : "produto.codigo_barras_etiqueta";
    mapped.exibir_quando = "se_preenchido";
    return mapped;
  }

  if (type === "barcode" && field === "produto.codigo" && internalNumberOnly) {
    mapped.tipo = "field";
    mapped.campo = "produto.codigo_legenda_etiqueta";
    mapped.exibir_quando = "se_preenchido";
    return mapped;
  }

  if (
    type === "field" &&
    IMPLICIT_BARCODE_FIELDS.includes(field) &&
    isCodeLegend(element, convertedLegacy)
  ) {
    mapped.campo = "produto.codigo_legenda_etiqueta";
    mapped.exibir_quando = "se_preenchido";
  }

  return mapped;
};

// Um único quadro do editor pode representar mais de uma origem de código na
// impressão. Os quadros derivados existem apenas na prévia: conservam o id e a
// geometria da definição original como uma única unidade editável.
export const resolveElementsForPrintOptions = (
  element,
  options = {},
  convertedLegacy = false
) => {
  if (!isSelectableBarcode(element, convertedLegacy)) {
    return [resolveElementForPrintOptions(element, options, convertedLegacy)];
  }

  const sources = activeBarcodeSources(options);
  if (sources.length < 2) {
    return [resolveElementForPrintOptions(element, options, convertedLegacy)];
  }

  const originalY = Number(element.y_mm || 0);
  const originalHeight = Number(element.altura_mm || 1);
  const height = originalHeight / sources.length;

  return sources.map((source, index) => ({
    ...element,
    id: `${element.id}__${source.suffix}`,
    tipo: source.type,
    campo: source.field,
    y_mm: originalY + index * height,
    altura_mm: height,
    exibir_quando: "se_preenchido",
  }));
};

export const resolvePreviewValues = (sampleValues = {}, options = {}) => {
  const values = { ...sampleValues };
  const reference = String(values["produto.referencia"] ?? "").trim();
  const internalCode = String(values["produto.codigo"] ?? "").trim();
  const barcode = String(values["produto.codigo_barras"] ?? "").trim();
  const price = values["produto.preco"];
  const weight = parseLocalizedNumber(values["produto.peso"]);
  const numericPrice = parseLocalizedNumber(price);

  values["produto.preco_kg"] =
    numericPrice !== null && weight !== null && weight > 0
      ? numericPrice / weight
      : null;

  if (hasOwn(options, "preco") && !options.preco) {
    values["produto.referencia_preco"] = "";
  } else if (price === null || price === undefined || price === "") {
    values["produto.referencia_preco"] = "";
  } else {
    if (numericPrice === null) {
      values["produto.referencia_preco"] = "";
    } else {
      const formattedPrice = `R$: ${formatLocalizedNumber(numericPrice, 2)}`;
      values["produto.referencia_preco"] = reference
        ? `${reference} - ${formattedPrice}`
        : formattedPrice;
    }
  }

  if (
    options.considerar_codigo_interno &&
    !options.codigo_interno_somente_numeracao &&
    internalCode
  ) {
    values["produto.codigo_barras_etiqueta"] = internalCode;
  } else if (options.considerar_referencia && reference) {
    values["produto.codigo_barras_etiqueta"] = reference;
  } else if (
    (!hasOwn(options, "cod_barras") || options.cod_barras) &&
    barcode
  ) {
    values["produto.codigo_barras_etiqueta"] = barcode;
  } else {
    values["produto.codigo_barras_etiqueta"] = "";
  }

  if (options.considerar_codigo_interno && internalCode) {
    values["produto.codigo_legenda_etiqueta"] = internalCode;
  } else if (options.considerar_referencia && reference) {
    values["produto.codigo_legenda_etiqueta"] = reference;
  } else if (
    (!hasOwn(options, "cod_barras") || options.cod_barras) &&
    barcode
  ) {
    values["produto.codigo_legenda_etiqueta"] = barcode;
  } else {
    values["produto.codigo_legenda_etiqueta"] = "";
  }
  values["produto.codigo_barras_a4054"] =
    values["produto.codigo_barras_etiqueta"];

  if (hasOwn(options, "texto_personalizado_etiqueta")) {
    values["impressao.texto_personalizado"] =
      options.texto_personalizado_etiqueta ?? "";
  }

  return values;
};

export const shouldDisplayPreview = (
  element,
  values = {},
  options = {},
  logoUrl = ""
) => {
  if (!element || element.visivel === false) return false;
  if (["line", "rectangle"].includes(element.tipo)) return true;
  if (element.tipo === "logo") {
    return (
      Boolean(String(logoUrl || "").trim()) && options.imprimir_logo !== false
    );
  }

  const value =
    element.tipo === "text" ? element.texto ?? "" : values[element.campo] ?? "";
  switch (element.exibir_quando || "se_preenchido") {
    case "sempre":
      return true;
    case "quando_preco":
      return Boolean(options.preco);
    case "quando_codigo_barras":
      return Boolean(options.cod_barras);
    case "quando_referencia":
      return Boolean(options.considerar_referencia);
    case "quando_codigo_interno":
      return Boolean(options.considerar_codigo_interno);
    case "quando_valor_kg":
      return Boolean(options.valor_por_kg || options.gerar_valor_kg);
    case "quando_logo":
      return Boolean(options.imprimir_logo);
    case "se_preenchido":
    default:
      return value !== null && value !== undefined && value !== "";
  }
};

export const isPriceModeElement = (element) =>
  element?.tipo === "field" && element?.campo === "produto.preco";

// Keep the monetary formatter shared with all existing fields; only the
// composition of the price lines is specific to the sales-price element.
// Work in already rounded cents so the commercial ending cannot undo the
// existing half-up rounding of the financed total and each installment.
export const adjustCommercialEnding = (cents, ending = "none") => {
  if (ending !== "0" && ending !== "9") return cents;
  const lastDigit = ((cents % 10) + 10) % 10;
  const target = Number(ending);
  return cents + ((target - lastDigit + 10) % 10);
};

export const priceDisplayLines = (element, values = {}) => {
  if (!isPriceModeElement(element) || !element.priceMode) return null;
  const raw = values[element.campo];
  if (raw === null || raw === undefined || raw === "") {
    return [previewValue(element, values)];
  }
  const price = parseLocalizedNumber(raw);
  if (price === null) return [previewValue(element, values)];

  const currencyElement = {
    ...element,
    formato: { ...element.formato, mascara: "moeda", casas_decimais: 2 },
  };
  const cash = `${previewValue(currencyElement, values)} à vista`;
  if (element.priceMode === "cash") return [cash];

  const installments = Number(element.installments);
  const increase = Number(element.installmentIncrease);
  if (
    !Number.isInteger(installments) ||
    installments < 1 ||
    !Number.isFinite(increase) ||
    increase < 0
  )
    return [cash];
  // Product prices and percentages use four decimal places. Round the
  // financed total to cents first, then one installment to cents (half up).
  const priceUnits = Math.round(price * 10000);
  const increaseUnits = Math.round(increase * 10000);
  const roundHalfUp = (value) =>
    Math.sign(value) * Math.floor(Math.abs(value) + 0.5);
  const totalCents = roundHalfUp(
    (priceUnits * (1000000 + increaseUnits)) / 100000000
  );
  const installmentCents = adjustCommercialEnding(
    roundHalfUp(totalCents / installments),
    element.commercialEnding
  );
  const installment = installmentCents / 100;
  const installmentText = `${installments}x de ${previewValue(currencyElement, {
    [element.campo]: installment,
  })}`;
  return element.priceMode === "cash_installment"
    ? [cash, installmentText]
    : [installmentText];
};

export const previewValue = (element, sampleValues = {}) => {
  let value =
    element.tipo === "text" ? element.texto : sampleValues[element.campo];
  value =
    value === undefined || value === null || value === ""
      ? element.formato?.texto_vazio || ""
      : String(value);

  const mask = element.formato?.mascara || "automatica";
  const automaticCurrencyFields = [
    "produto.preco",
    "produto.preco_kg",
    "produto.preco_atacado",
    "produto.custo",
    "produto.valor_prazo",
  ];
  const automaticDateFields = ["produto.validade", "impressao.data"];

  if (
    value !== "" &&
    (mask === "moeda" ||
      (mask === "automatica" &&
        automaticCurrencyFields.includes(element.campo)))
  ) {
    const numeric = parseLocalizedNumber(value);
    if (numeric !== null) {
      value = `R$ ${formatLocalizedNumber(
        numeric,
        element.formato?.casas_decimais
      )}`;
    }
  } else if (value !== "" && mask === "numero") {
    const numeric = parseLocalizedNumber(value);
    if (numeric !== null) {
      value = formatLocalizedNumber(numeric, element.formato?.casas_decimais);
    }
  } else if (
    value !== "" &&
    (mask === "data" ||
      (mask === "automatica" && automaticDateFields.includes(element.campo)))
  ) {
    value = formatDate(value);
  }

  switch (element.formato?.transformacao) {
    case "maiusculas":
      value = value.toUpperCase();
      break;
    case "minusculas":
      value = value.toLowerCase();
      break;
    case "capitalizar":
      value = value
        .toLowerCase()
        .replace(/(^|\s)\S/g, (letter) => letter.toUpperCase());
      break;
    default:
      break;
  }

  const limit = Number(element.formato?.limite_caracteres || 0);
  if (limit > 0 && value.length > limit)
    value = `${value.slice(0, Math.max(1, limit - 1))}…`;

  return `${element.formato?.prefixo || ""}${value}${
    element.formato?.sufixo || ""
  }`;
};

export const formatFieldSample = (field = {}) => {
  const element = {
    tipo: "field",
    campo: field.key,
    formato: {
      mascara: field.format || "automatica",
      casas_decimais: 2,
      transformacao: "nenhuma",
      limite_caracteres: 0,
      prefixo: "",
      sufixo: "",
      texto_vazio: "",
    },
  };

  return previewValue(element, { [field.key]: field.sample });
};

const parseLocalizedNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  let normalized = String(value)
    .replace(/[^0-9,.-]/g, "")
    .trim();
  if (!normalized) return null;
  if (normalized.includes(",")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  }

  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatLocalizedNumber = (value, decimalPlaces = 2) => {
  const decimals = Math.max(0, Math.min(4, Number(decimalPlaces ?? 2)));
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatDate = (value) => {
  const raw = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  const isoDate = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  return isoDate ? `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}` : raw;
};

export const layoutWarnings = (definition = {}) => {
  const page = definition.pagina || {};
  const label = definition.etiqueta || {};
  const warnings = [];
  const usedWidth =
    Number(page.margem_esquerda_mm || 0) +
    Number(page.margem_direita_mm || 0) +
    Number(page.colunas || 1) * Number(label.largura_mm || 0) +
    Math.max(0, Number(page.colunas || 1) - 1) *
      Number(page.espacamento_horizontal_mm || 0);
  const usedHeight =
    Number(page.margem_superior_mm || 0) +
    Number(page.margem_inferior_mm || 0) +
    Number(page.linhas || 1) * Number(label.altura_mm || 0) +
    Math.max(0, Number(page.linhas || 1) - 1) *
      Number(page.espacamento_vertical_mm || 0);

  if (usedWidth > Number(page.largura_mm || 0) + 0.5) {
    warnings.push("As colunas ultrapassam a largura da página.");
  }
  if (usedHeight > Number(page.altura_mm || 0) + 0.5) {
    warnings.push("As linhas ultrapassam a altura da página.");
  }

  const outside = (definition.elementos || []).some(
    (element) =>
      Number(element.x_mm) < 0 ||
      Number(element.y_mm) < 0 ||
      Number(element.x_mm) + Number(element.largura_mm) >
        Number(label.largura_mm) + 0.1 ||
      Number(element.y_mm) + Number(element.altura_mm) >
        Number(label.altura_mm) + 0.1
  );
  if (outside)
    warnings.push("Há elementos fora da etiqueta e eles podem ser cortados.");

  return warnings;
};

export const scopeLabel = (model = {}) => {
  if (model.escopo !== "empresa") return "Todas as empresas";
  return (
    model.empresa?.nome_fantasia ||
    model.empresa?.nome ||
    `Empresa ${model.id_empresa}`
  );
};
